import { useEffect, useRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { PlaceResult } from "@/lib/api/misc";
import { getKundaliPrefill, type KundaliPrefill } from "@/lib/api/kundali";

/** Read a reading_id from the URL (hash first — the reactivation nudge's
 * collect-inputs link carries it in the hash alongside intent/next_report — then
 * query as a fallback). Client-only. */
function readReadingIdFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const rawHash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const fromHash = new URLSearchParams(rawHash).get("reading_id");
  if (fromHash) return fromHash;
  return new URLSearchParams(window.location.search).get("reading_id");
}

/** Read LITERAL birth-detail fields the seeker already entered on a calculator
 * page (numerology / rashi / mangal-dosha / dasamsa), carried in the
 * collect-inputs link's hash (name / dob / time / place). Unlike `reading_id`
 * (a stored chart that needs a fetch), these are the exact values the seeker
 * just typed, so they apply with NO network call — removing the "why am I
 * re-entering what I just typed?" friction on the highest-CTR calculator pages.
 * The hash fragment stays client-side (never sent to the origin), so the DOB is
 * not written to any server log. Client-only. */
export function readDirectPrefillFromUrl(): Partial<KundaliPrefill> {
  if (typeof window === "undefined") return {};
  const rawHash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const hp = new URLSearchParams(rawHash);
  const qp = new URLSearchParams(window.location.search);
  const get = (k: string) => (hp.get(k) ?? qp.get(k) ?? "").trim();
  const out: Partial<KundaliPrefill> = {};
  const name = get("name");
  const dob = get("dob");
  const time = get("time");
  const place = get("place");
  if (name) out.name = name;
  if (dob) out.birth_date = dob;
  if (time) out.birth_time = time;
  if (place) out.birth_place = place;
  return out;
}

export interface KundaliPrefillSetters {
  setName: Dispatch<SetStateAction<string>>;
  setBirthDate: Dispatch<SetStateAction<string>>;
  setBirthTime: Dispatch<SetStateAction<string>>;
  setBirthPlace: Dispatch<SetStateAction<string>>;
  setSelectedPlace: Dispatch<SetStateAction<PlaceResult | null>>;
}

/** Apply a prefill payload to the form's state — each field only when present. */
function applyPrefill(p: Partial<KundaliPrefill>, s: KundaliPrefillSetters): void {
  if (p.name) s.setName(p.name);
  if (p.birth_date) s.setBirthDate(p.birth_date);
  if (p.birth_time) s.setBirthTime(p.birth_time);
  if (p.birth_place) s.setBirthPlace(p.birth_place);
  if (p.birth_place && p.latitude != null && p.longitude != null) {
    s.setSelectedPlace({
      geoname_id: 0,
      name: p.birth_place,
      display: p.birth_place,
      latitude: p.latitude,
      longitude: p.longitude,
      timezone: p.timezone || "",
      country: "",
      population: 0,
    });
  }
}

/**
 * One-tap re-entry: when the URL carries a `reading_id` (a reactivation-nudge
 * landing), prefill the birth-details form from the seeker's STORED chart so they
 * confirm + pay instead of re-typing. Additive + best-effort — a missing id or a
 * failed fetch leaves the form empty (exactly today's behavior), so it can never
 * regress the normal /free-kundali load (no reading_id → the effect no-ops before
 * any fetch). Runs once. No layout change → no CLS (rule 58); the fetch fires only
 * on nudge landings, never on organic loads (rule 62).
 */
export function useReadingPrefill({
  setName,
  setBirthDate,
  setBirthTime,
  setBirthPlace,
  setSelectedPlace,
}: KundaliPrefillSetters): void {
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const setters = { setName, setBirthDate, setBirthTime, setBirthPlace, setSelectedPlace };
    // 1. Literal fields the seeker carried over from a calculator page (no fetch).
    const direct = readDirectPrefillFromUrl();
    if (direct.name || direct.birth_date || direct.birth_time || direct.birth_place) {
      applyPrefill(direct, setters);
    }
    // 2. reading_id → fetch the stored chart (reactivation-nudge landing).
    const readingId = readReadingIdFromUrl();
    if (!readingId) return;
    let cancelled = false;
    getKundaliPrefill(readingId)
      .then((p) => {
        if (!cancelled && p) {
          applyPrefill(p, setters);
        }
      })
      .catch(() => {
        // best-effort: leave the form empty (today's behavior)
      });
    return () => {
      cancelled = true;
    };
  }, [setName, setBirthDate, setBirthTime, setBirthPlace, setSelectedPlace]);
}
